// Mantiene la escena 3D sincronizada con el proyecto: una vista por pieza y una malla por cable.
import * as THREE from 'three';
import { MODELS } from './models/index.js';
import { getDef, getFootprint, getPins } from '../parts/index.js';
import { partProps, toWorld, rotDir } from '../connectivity.js';
import { buildWire, disposeWire, setWireHighlight } from './wires.js';
import { disposeObject, visibleBounds } from './kit.js';

function rebuildKey(part, props) {
  const def = getDef(part.type);
  const keep = {};
  for (const p of def.props) if (!p.runtime || p.rebuild) keep[p.key] = props[p.key];
  return part.type + JSON.stringify(keep);
}

export class World {
  constructor(stage) {
    this.stage = stage;
    this.views = new Map();
    this.wires = new Map();
    this.partsRoot = new THREE.Group();
    this.wiresRoot = new THREE.Group();
    stage.world.add(this.partsRoot, this.wiresRoot);
    this.layout = null;
    this.selection = null;
    this.hover = null;
  }

  createView(part, props) {
    const def = getDef(part.type);
    const build = MODELS[part.type];
    let view;
    try {
      view = build ? build(def, props) : placeholder(def, props);
    } catch (e) {
      // Un modelo con errores no debe tumbar toda la escena: se dibuja una caja en su lugar.
      console.error(`Modelo 3D de ${part.type}:`, e);
      view = placeholder(def, props);
    }
    view.root.traverse((o) => {
      o.userData.partId = part.id;
    });
    view.root.userData.partId = part.id;
    return view;
  }

  sync(project, layout) {
    this.layout = layout;
    const seen = new Set();
    for (const part of project.parts) {
      seen.add(part.id);
      const props = partProps(part);
      const key = rebuildKey(part, props);
      let entry = this.views.get(part.id);
      if (!entry || entry.key !== key) {
        if (entry) this.removeView(part.id);
        const view = this.createView(part, props);
        entry = { view, key, props: null, part };
        this.views.set(part.id, entry);
        this.partsRoot.add(view.root);
        if (entry.lastVisual && view.update) view.update(entry.lastVisual);
      }
      entry.part = part;
      if (entry.props !== props) {
        if (entry.view.setProps) {
          entry.view.setProps(props);
          this.kick(part.id);
        }
        entry.props = props;
      }
      const y = layout.y.get(part.id) || 0;
      entry.view.root.position.set(part.x, y, part.z);
      entry.view.root.rotation.y = ((part.rot || 0) * Math.PI) / 180;
    }
    for (const id of [...this.views.keys()]) if (!seen.has(id)) this.removeView(id);
    this.syncWires(project);
    this.stage.invalidate();
  }

  removeView(id) {
    const entry = this.views.get(id);
    if (!entry) return;
    this.partsRoot.remove(entry.view.root);
    if (entry.view.dispose) entry.view.dispose();
    disposeObject(entry.view.root);
    this.views.delete(id);
  }

  /** Posición y dirección de salida de un pin en el mundo. */
  pinPose(project, ref) {
    const part = project.parts.find((p) => p.id === ref.part);
    if (!part) return null;
    const props = partProps(part);
    const pin = pinByName(part.type, props, ref.pin);
    if (!pin) return null;
    const y = (this.layout && this.layout.y.get(part.id)) || 0;
    const [wx, wz] = toWorld(part, pin.x, pin.z);
    const dir = pin.dir ? rotDir(pin.dir, part.rot || 0) : [0, 1, 0];
    return { pos: [wx, y + pin.y, wz], dir, pin };
  }

  syncWires(project) {
    const seen = new Set();
    for (const w of project.wires) {
      seen.add(w.id);
      const a = this.pinPose(project, w.a);
      const b = this.pinPose(project, w.b);
      const entry = this.wires.get(w.id);
      if (!a || !b) {
        if (entry) this.removeWire(w.id);
        continue;
      }
      const key = JSON.stringify([a.pos, a.dir, b.pos, b.dir, w.color]);
      if (entry && entry.key === key) continue;
      if (entry) this.removeWire(w.id);
      const mesh = buildWire(a.pos, a.dir, b.pos, b.dir, w.color);
      mesh.traverse((o) => {
        o.userData.wireId = w.id;
      });
      this.wiresRoot.add(mesh);
      this.wires.set(w.id, { mesh, key });
      if (this.selection && this.selection.type === 'wire' && this.selection.id === w.id) setWireHighlight(mesh, 2);
    }
    for (const id of [...this.wires.keys()]) if (!seen.has(id)) this.removeWire(id);
  }

  removeWire(id) {
    const e = this.wires.get(id);
    if (!e) return;
    this.wiresRoot.remove(e.mesh);
    disposeWire(e.mesh);
    this.wires.delete(id);
  }

  setWireState(id, level) {
    const e = this.wires.get(id);
    if (e) setWireHighlight(e.mesh, level);
    this.stage.invalidate();
  }

  applyVisual(id, visual) {
    const entry = this.views.get(id);
    if (!entry) return;
    entry.lastVisual = visual;
    if (entry.view.update) entry.view.update(visual);
    this.kick(id);
    this.stage.invalidate();
  }

  /** Arranca la animación continua de una vista (si tiene). */
  kick(id) {
    const entry = this.views.get(id);
    if (!entry || !entry.view.animate) return;
    if (!entry.anim) entry.anim = (dt) => (this.views.get(id) === entry ? entry.view.animate(dt) : false);
    this.stage.animate(entry.anim);
  }

  resetVisuals() {
    for (const entry of this.views.values()) {
      entry.lastVisual = null;
      if (entry.view.reset) entry.view.reset();
    }
    this.stage.invalidate();
  }

  /** Caja que envuelve todas las piezas (para encuadrar la cámara). */
  bounds() {
    return visibleBounds([...this.views.values()].map((e) => e.view.root));
  }

  footprintCorners(part) {
    const f = getFootprint(part.type, partProps(part));
    const pts = [[-f.w / 2, -f.d / 2], [f.w / 2, -f.d / 2], [f.w / 2, f.d / 2], [-f.w / 2, f.d / 2]];
    return pts.map(([x, z]) => toWorld(part, x + f.x, z + f.z));
  }
}

const pinMaps = new WeakMap();
function pinByName(type, props, name) {
  let m = pinMaps.get(props);
  if (!m) {
    m = new Map(getPins(type, props).map((p) => [p.name, p]));
    pinMaps.set(props, m);
  }
  return m.get(name);
}

function placeholder(def, props) {
  const f = getFootprint(def.type, props);
  const g = new THREE.Group();
  const m = new THREE.Mesh(new THREE.BoxGeometry(f.w, f.h, f.d), new THREE.MeshStandardMaterial({ color: '#556070', roughness: 0.7 }));
  m.position.set(f.x, f.h / 2, f.z);
  g.add(m);
  return { root: g };
}
